import { Position } from '@application/Interfaces/Hooks/useScrollPosition/useScrollPosition'
import { BlogListItemType, HelpCenterListItemType } from '@src/Application/Types/ComponentTypes'
import { Nullable } from '@src/Application/Types/Opt'
import {
    ARTICLE_SECTION_HEADING_REGEXP,
    ARTICLE_SECTION_SUBMENU_REGEXP,
    NUMBERED_LIST_REGEXP,
    SIDEBAR_POSITION,
    START_WHITESPACE_REGEXP,
    STRIP_HTML_TAGS,
} from '@src/Core/Constants/PageConstants'

export type Heading = {
    title: string
    subHeadings: string[]
}

export const findContentGuideHeadings = (html: string): Heading[] => {
    const mainHeadings = html.match(ARTICLE_SECTION_HEADING_REGEXP) ?? []

    const headings: Heading[] = []

    mainHeadings.forEach((h, index) => {
        let subHeadings = []

        if (index < mainHeadings.length - 1) {
            const subHtml = html.substring(html.indexOf(h), html.indexOf(mainHeadings[index + 1]))

            subHeadings = subHtml.match(ARTICLE_SECTION_SUBMENU_REGEXP) ?? []
        } else {
            const subHtml = html.substring(html.indexOf(h))
            subHeadings = subHtml.match(ARTICLE_SECTION_SUBMENU_REGEXP) ?? []
        }

        headings.push({
            title: h
                .replace(STRIP_HTML_TAGS, '')
                .replace(NUMBERED_LIST_REGEXP, '')
                .replace(START_WHITESPACE_REGEXP, ''),
            subHeadings: subHeadings.map((page) => page.replace(STRIP_HTML_TAGS, '')),
        })
    })

    return headings
}

const TOP = 120

type OnScrollType = {
    setSidebarState: (state: Nullable<string>) => void
    wrapperRef: React.RefObject<HTMLDivElement>
    sidebarRef: React.RefObject<HTMLDivElement>
}

// updates content panel position based on scroll position
export const onScroll =
    ({ setSidebarState, wrapperRef, sidebarRef }: OnScrollType) =>
    (): void => {
        const rect = wrapperRef?.current?.getBoundingClientRect()
        const sidebarHeight = sidebarRef?.current?.clientHeight

        if (rect && sidebarHeight && rect.top - TOP < 0) {
            if (rect.height + rect.top - TOP < sidebarHeight) {
                setSidebarState(SIDEBAR_POSITION.BOTTOM)
            } else {
                setSidebarState(SIDEBAR_POSITION.FIXED)
                sidebarRef.current.style.width = `${rect.width}px` // eslint-disable-line no-param-reassign
            }
        } else {
            setSidebarState(null)
        }
    }

type OnProgressScroll = {
    setProgressState: (state: number) => void
    setProgressPositionState: (state: number) => void
    wrapperRef: React.RefObject<HTMLDivElement>
}

export const onProgressScroll =
    ({ setProgressState, setProgressPositionState, wrapperRef }: OnProgressScroll) =>
    (scrollPosition: Position): void => {
        const contentElement = wrapperRef?.current

        if (!contentElement) return

        const rect = contentElement.getBoundingClientRect()
        const top = contentElement.offsetTop

        const totalProgressHeight = top + rect.height - window.innerHeight

        const progress = Math.min(100, (100 * scrollPosition.scrollY) / totalProgressHeight)

        setProgressState(progress)

        // TODO figure out better way to find fixed element
        const quoteForm = document.querySelector('div.sticky')

        if (quoteForm) {
            setProgressPositionState(quoteForm.clientHeight)
        } else {
            setProgressPositionState(0)
        }
    }

export const getRelatedArticles = (
    items: (BlogListItemType | HelpCenterListItemType)[],
    title: string
): (BlogListItemType | HelpCenterListItemType)[] => {
    return items
        ?.filter((item) => item.title.data?.title !== title)
        ?.sort((a, b) => {
            const ta = +new Date(a.publishedAt)
            const tb = +new Date(b.publishedAt)

            return ta < tb ? 1 : ta > tb ? -1 : 0
        })
        ?.slice(0, 3)
}
